import React, { useState, useEffect, useContext } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { TagManager } from '@navent-jobs/config'
import { makeFichaAvisoURL } from '@navent-jobs/interlinking'
import { ThemeContext } from 'styled-components'

// constants
import features from '../../../../constants/features-per-country'
import { SITE_ID } from '../../../../constants'
import { iaOrigenRecomendados } from '../../../../constants/origen-avisos-recomendados'

// components
import EstadosPostulacion from './components/estados-postulacion'
import PlaceholderEstado from './components/estados-postulacion/placeholder'

// modals
import ModalPostulacionExitosa from '../../modals/modal-postulacion-exitosa'
import ModalPreguntas from '../../modals/modal-preguntas'
import ModalAvisos from '../../../../components/modals/modal-avisos'
import ModalActualizacionCv from '../../modals/modal-actualizacion-cv'

// mixins
import { PostularBarContainer, WordingContainer, IconCustom, PostuladoText } from './mixins'

// types
import { PostularBarProps } from './types'

// tracking
import { DataLayers } from '../../../../constants/gtm-events-postular-bar'
import { DataLayersPostPostulacion } from '../../../../constants/gtm-events-post-postulacion'

// store
import { sendActivationEmail } from '../../../../redux/actions/activation-email'
import { postToApply, sendAnswers } from '../../../../redux/actions/postular-bar'
import { showSnackbarError } from '../../../../redux/actions/applicant/curriculum/snackbar'
import { getRecomendados } from '../../../../redux/actions/recomendados'
import { getDatosPersonales } from '../../../../redux/actions/applicant/applicant'
import { DatosPersonales } from '../../../../redux/types/applicant/commons'

const preguntasObligatorias = features[SITE_ID || 'BMAR'].postulacion?.preguntasObligatorias?.enabled
const withSubscriptionCheck = features[SITE_ID || 'BMAR'].fichaAviso?.postPostulacion?.subscriptionCheck
const searchAvisosV2Enabled = features[SITE_ID || 'BMAR'].searchAvisosV2.enabled

const CONFIDENCIAL = 'Confidencial'
const MODAL_PREGUNTAS_AVISOS = 'modal-preguntas-aviso'
const MODAL_ACTUALIZACION_CV = 'modal-actualizacion-cv'
const RECOMMENDED_JOB_POSTING_AMOUNT = 5
const IA_ORIGEN = iaOrigenRecomendados.postPostulacion

const redirect = url => {
  window.location.href = url
}

const newTab = url => {
  window.open(url)
}

const pushParameterIsFeria = (feria, parameterData) => {
  if (feria) {
    parameterData.push('origen_feria')
  }
}

const openFichaInNewTab = redireccionUrl => {
  if (redireccionUrl) {
    newTab(redireccionUrl)
  }
}

const onClickAviso = (event, url) => {
  if (!(event.ctrlKey || event.metaKey)) {
    event.preventDefault()
    window.open(url, '_self')
  }
}

const openModalAvisos = (avisosSimilares, setVisibleModal, hideModal, visibleModalPreguntas) => {
  if (visibleModalPreguntas && avisosSimilares && avisosSimilares?.length > 0) {
    setVisibleModal('modal-avisos')
  } else {
    hideModal()
  }
}

const openModalActualizacionCv = (datosPersonalesCompletos: boolean, setVisibleModal, hideModal) => {
  if (!datosPersonalesCompletos) {
    setVisibleModal(MODAL_ACTUALIZACION_CV)
  } else {
    hideModal()
  }
}

const openModalPostPostulacion = (
  datosPersonales: DatosPersonales | undefined,
  setVisibleModal,
  hideModal,
  avisosSimilares,
  visibleModalPreguntas
) => {
  const datosPersonalesCompletos =
    !!datosPersonales?.nombre &&
    !!datosPersonales?.apellido &&
    !!datosPersonales?.fechaNacimiento &&
    (!!datosPersonales?.telefonoCelular.numero || !!datosPersonales?.telefonoFijo.numero) &&
    !!datosPersonales?.genero
  if (datosPersonales && !datosPersonalesCompletos) {
    openModalActualizacionCv(datosPersonalesCompletos, setVisibleModal, hideModal)
  } else {
    openModalAvisos(avisosSimilares, setVisibleModal, hideModal, visibleModalPreguntas)
  }
}

const openModalExitoso = (estadoResp, setVisibleModal) => {
  if (estadoResp === 'REALIZADA' && !window.location.pathname.includes('/empleos/')) {
    /**
     * Only opens modal when you are not in job application's detail
     * and if the application was successful.
     */
    setVisibleModal('modal-postulacion-exitosa')
  }
}

const urlToReturnMaker = (url, id) => {
  if (url.includes(id)) {
    return `${url}/${id}`
  }

  return url
}

const functionSendAnswers = (
  dataPostulacion,
  postPostulacion,
  postulacionState,
  respuestas,
  dispatch,
  setLoadingAnswers
) => {
  const { idPostulacion } = postPostulacion?.idPostulacion ? postPostulacion : postulacionState || {}
  if (respuestas) {
    if (!idPostulacion && preguntasObligatorias) {
      setLoadingAnswers(true)
      dataPostulacion.respuestas = respuestas
      dataPostulacion.preguntasObligatorias = preguntasObligatorias
      dispatch(postToApply(dataPostulacion))
      dispatch(getDatosPersonales())
    } else {
      dispatch(sendAnswers({ idPostulacion, respuestas }))
      dispatch(getDatosPersonales())
    }
  }
}

const handlePostToApply = (aviso, setVisibleModal, setLoading, dispatch, getDataPostulacion) => {
  if (preguntasObligatorias && aviso?.preguntas?.length > 0) {
    setVisibleModal('modal-preguntas-pendientes')
  } else {
    setLoading(true)
    dispatch(postToApply(getDataPostulacion()))
    dispatch(getDatosPersonales())
  }
}

const handlePostApplyError = (postApplyStates, visibleModal, setLoadingAnswers, dispatch) => {
  if (postApplyStates?.error && visibleModal === MODAL_PREGUNTAS_AVISOS) {
    setLoadingAnswers(false)
    dispatch(showSnackbarError('Ha ocurrido un error'))
  }
}

const PostularBar = ({
  aviso,
  productoLookAndFeel,
  postulacion,
  fichaAvisoService,
  feria,
  postularme,
  desdeEmail,
  urlMaker,
  postApply,
  postApplyStates,
  sendAnswersStates,
  emailApplicant,
  idApplicant,
  avisoVencido,
  isNavResized,
  avisosSimilares,
  notificationSettingsStore,
  recomendadosStore,
  setSalarioPretendido,
  salarioBrutoPretendido,
  datosPersonales,
  postulacionRapida = false,
}: PostularBarProps) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { t } = useTranslation()
  const { colors } = useContext<{ colors }>(ThemeContext)

  const idAviso = aviso?.id
  const salarioRequerido = aviso?.requisitos?.salario
  const avisoConPreguntas = aviso?.preguntas?.length > 0
  const redireccionUrl = aviso?.redireccionURL

  const [postulacionState, setPostulacion] = useState(postulacion)
  const [desuscripto, setDesuscripto] = useState<boolean | undefined>(false)
  const [postPostulacion, setPostPostulacion] = useState({
    estado: '',
    fecha: '',
    idPostulacion: 0,
    motivoEspera: '',
  })
  const [actualizarSalario, setActualizarSalario] = useState(true)
  const [answersSent, setAnswersSent] = useState(false)
  const [openedModalPreguntas, setOpenedModalPreguntas] = useState(false)

  const [visibleModal, setVisibleModal] = useState(avisoVencido ? 'modal-avisos-sugeridos' : '')
  const hideModal = () => setVisibleModal('')
  const visibleModalPreguntas = visibleModal === MODAL_PREGUNTAS_AVISOS

  const [isLoading, setLoading] = useState(false)
  const [isLoadingAnswers, setLoadingAnswers] = useState(false)

  const iaOrigen = `ia_origen=${IA_ORIGEN}`
  const labsParamsStr = recomendadosStore?.labsParams
    ? `?${new URLSearchParams(recomendadosStore?.labsParams).toString()}&${iaOrigen}`
    : `?${iaOrigen}`

  const parameterData: any = []

  const handleActivateMail = () => {
    setLoading(true)
    dispatch(
      sendActivationEmail({
        fetchMail: 'activar-cuenta',
        applicantMail: emailApplicant,
      })
    )
    setLoading(false)
  }

  const getDataPostulacion = () => {
    return { actualizarSalario, id: aviso.id, salarioBrutoPretendido, feria }
  }

  const handlePostularme = async () => {
    /**
     * Si no está logueado al tocar el botón para postularse, se redirige a /login
     */
    if (!emailApplicant) {
      // Agregamos idAviso a la URL si no existe
      const urlToReturn = urlToReturnMaker(location.pathname, idAviso)

      parameterData.push('postularme')

      pushParameterIsFeria(feria, parameterData)

      const parameter = parameterData.join('_')

      const urlToRedirect = `/login?returnTo=${urlToReturn}#${parameter}`
      redirect(urlToRedirect)

      return
    }

    handlePostToApply(aviso, setVisibleModal, setLoading, dispatch, getDataPostulacion)
  }

  const handleSendAnswers = async ({ respuestas }) => {
    functionSendAnswers(
      getDataPostulacion(),
      postPostulacion,
      postulacionState,
      respuestas,
      dispatch,
      setLoadingAnswers
    )
  }

  useEffect(() => {
    if (aviso && aviso.id && postApplyStates?.loading) {
      dispatch(
        getRecomendados({
          avisoId: Number(aviso.id),
          limit: RECOMMENDED_JOB_POSTING_AMOUNT,
          source: 'post_postulacion',
        })
      )
    }
  }, [postApplyStates?.loading])

  useEffect(() => {
    if (postApplyStates?.success) {
      const response = postApply
      const idPostulacion = response.id
      const estadoResp = response.estado.toUpperCase()

      TagManager.Push(
        DataLayers.postulacion({
          idAviso: aviso?.id,
          idPostulacion,
          idUsuario: idApplicant,
          estado: response.estado.toUpperCase(),
          desdeEmail,
          avisoConPreguntas,
          actualizaCV: actualizarSalario,
        })
      )

      setPostulacion({
        estado: estadoResp,
        fecha: moment().format('DD-MM-YYYY'),
        idPostulacion: response.id,
        motivoEspera: response.motivoEspera || '',
      })

      setDesuscripto(response.desuscripto)

      setPostPostulacion({
        estado: estadoResp,
        fecha: moment().format('DD-MM-YYYY'),
        idPostulacion: response.id,
        motivoEspera: response.motivoEspera || '',
      })

      setLoading(false)
      setLoadingAnswers(false)

      /**
       * Si el aviso tiene redireccionUrl, no redirige a carta, se
       * actualiza el componente PostularBar segun el estado y abre
       * un tab nuevo con esa url propia
       */
      openFichaInNewTab(redireccionUrl)

      if (avisoConPreguntas) {
        setOpenedModalPreguntas(true)
        setVisibleModal(MODAL_PREGUNTAS_AVISOS)
        DataLayersPostPostulacion.postLeadImpresion()
        return
      }

      openModalPostPostulacion(
        datosPersonales,
        setVisibleModal,
        hideModal,
        avisosSimilares,
        !avisoConPreguntas || visibleModalPreguntas
      )

      openModalExitoso(estadoResp, setVisibleModal)
    }

    handlePostApplyError(postApplyStates, visibleModal, setLoadingAnswers, dispatch)
  }, [postApplyStates])

  useEffect(() => {
    if (sendAnswersStates?.success) {
      setAnswersSent(true)
      openModalPostPostulacion(datosPersonales, setVisibleModal, hideModal, avisosSimilares, visibleModalPreguntas)
    }
    if (sendAnswersStates?.error) {
      dispatch(showSnackbarError('Ha ocurrido un error'))
    }
    setLoadingAnswers(sendAnswersStates?.loading)
  }, [sendAnswersStates])

  useEffect(() => {
    setPostulacion(postulacion)
  }, [postulacion])

  if (!aviso) {
    return <PlaceholderEstado />
  }

  if (postularme && !salarioRequerido) {
    handlePostularme()
  }

  return (
    <>
      <PostularBarContainer isNavResized={isNavResized} id="section-postular-bar">
        <EstadosPostulacion
          aviso={aviso}
          avisoFinalizado={avisoVencido}
          postulacionState={postulacionState}
          postPostulacion={postPostulacion}
          applicantEmail={emailApplicant}
          redirect={redirect}
          handleActivateMail={handleActivateMail}
          isLoading={isLoading}
          urlMaker={urlMaker}
          productoLookAndFeel={productoLookAndFeel}
          actualizarSalario={actualizarSalario}
          setActualizarSalario={setActualizarSalario}
          salarioBrutoPretendido={salarioBrutoPretendido}
          setSalarioPretendido={setSalarioPretendido}
          salarioRequerido={salarioRequerido}
          handlePostularme={handlePostularme}
          setVisibleModalPreguntasPendientes={() => setVisibleModal('modal-preguntas-pendientes')}
          showPreguntasPendientes={
            !answersSent &&
            !avisoVencido &&
            ((!postulacionState?.tienePreguntasPendientes && openedModalPreguntas) ||
              postulacionState?.tienePreguntasPendientes)
          }
          postulacionRapida={postulacionRapida}
        />
      </PostularBarContainer>

      <ModalPreguntas
        width={800}
        closeModal={() => {
          hideModal()
          openModalPostPostulacion(datosPersonales, setVisibleModal, hideModal, avisosSimilares, visibleModalPreguntas)
        }}
        visible={visibleModalPreguntas || visibleModal === 'modal-preguntas-pendientes'}
        preguntas={aviso?.preguntas}
        sendAnswers={handleSendAnswers}
        loading={isLoadingAnswers}
        preguntasObligatorias={preguntasObligatorias}
      />

      <ModalActualizacionCv closeModal={hideModal} visible={visibleModal === MODAL_ACTUALIZACION_CV} />

      <ModalPostulacionExitosa
        width={496}
        closeModal={hideModal}
        visible={visibleModal === 'modal-postulacion-exitosa'}
        fichaAvisoService={fichaAvisoService}
        idAviso={aviso?.id}
      />

      <ModalAvisos
        id="modal-avisos"
        width={920}
        visible={visibleModal === 'modal-avisos'}
        closeModal={hideModal}
        primaryButtonAction={urlMaker({
          area: searchAvisosV2Enabled ? aviso?.area.idSemantico : aviso?.area?.stringId,
          provincia: searchAvisosV2Enabled
            ? aviso?.localizacion?.provinciaIdSemantico
            : aviso?.localizacion?.provinciaStringId,
        })}
        primaryButtonText={t('fichaAviso.modalAvisos.primaryButtonText')}
        secondaryButtonAction={hideModal}
        secondaryButtonText={t('fichaAviso.modalAvisos.secondaryButtonText')}
        title={
          <WordingContainer>
            <IconCustom
              name="icon-light-checkbox-checked"
              size="32"
              color={colors.secondary.normal}
              marginRight="4px"
            />
            <PostuladoText>{t('fichaAviso.modalAvisos.title')}</PostuladoText>
          </WordingContainer>
        }
        subtitle={t('fichaAviso.modalAvisos.subtitle')}
        avisos={recomendadosStore?.avisos}
        onClickAviso={(event, avisoSeleccionado) => {
          onClickAviso(
            event,
            `${makeFichaAvisoURL({
              ...avisoSeleccionado,
              empresa: {
                denominacion: avisoSeleccionado.empresa,
                confidencial: avisoSeleccionado.empresa === CONFIDENCIAL,
              },
            })}${labsParamsStr}`
          )
        }}
        labsParams={labsParamsStr}
        withSubscriptionCheck={withSubscriptionCheck}
        desuscripto={desuscripto}
        notificationSettingsStore={notificationSettingsStore}
      />
    </>
  )
}

const states = ({
  postularBarStore,
  activeAvisoStore,
  notificationSettingsStore,
  recomendadosStore,
  applicantStore,
}) => {
  const { data: postApply, states: postApplyStates } = postularBarStore?.PostToApply
  const { states: sendAnswersStates } = postularBarStore?.SendAnswers

  return {
    postApply,
    postApplyStates,
    sendAnswersStates,
    avisosSimilares: activeAvisoStore.avisosSimilares,
    notificationSettingsStore,
    recomendadosStore,
    datosPersonales: applicantStore.datosPersonales,
  }
}

export default connect(states)(PostularBar)
